import { Component, ContentChild, ElementRef, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import {
  GroupType,
  PredictorGroupGames,
  PredictorSelectionViewModel,
  PredictorSelectionModel,
  PredictorSelectionQuestionViewModel,
  AnswerDisplay,
  GamesGroupService,
  ExternalPredictorGroupGames,
  UnprocessedPredictorAnswer,
} from '@fgb/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom } from 'rxjs';
import { GamePredictorThanksForPlayingModalComponent } from '../game-predictor-thanks-for-playing-modal/game-predictor-thanks-for-playing-modal.component';

@Component({
  selector: 'fgbcl-game-predictor-question-list',
  templateUrl: './game-predictor-question-list.component.html',
  styleUrl: './game-predictor-question-list.component.scss',
})
export class GamePredictorQuestionListComponent implements OnInit {
  @Input() predictor: PredictorGroupGames | ExternalPredictorGroupGames;
  @Input() endDate: Date;
  @Input() groupId: number;
  @Input() predictorStatus: string;
  @Input() isExternal: boolean = false;
  sharedRedirectURL: string = 'games/predictor';
  groupType = GroupType.Predictor;
  isClickOnsavePrediction: boolean = false;
  predictionsSubmitted: boolean = false;
  editPredictions: boolean = true;
  predictorSelectionModel: PredictorSelectionViewModel = { Questions: [] };
  predictorExpired: boolean = false;
  @Output() unsavedChangesEvent = new EventEmitter<boolean>(); // Output to notify the parent component
  @Output() closeModal = new EventEmitter();
  @Output() externalPredictorSubmitted = new EventEmitter<boolean>();
  loadingState: boolean;
  @ViewChild('content', { static: true }) content: ElementRef;
  @ContentChild('submissionFormContent', { static: false }) submissionFormTemplate: TemplateRef<any>;
  modalRef: NgbModalRef;

  constructor(
    private gamesGroupService: GamesGroupService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.predictor.Questions.forEach((question) => {
      if (question.AnswerDisplayType === AnswerDisplay.Score || question.AnswerDisplayType === AnswerDisplay.Counter) {
        let predictorQuestionSelection: PredictorSelectionQuestionViewModel = {
          QuestionId: question.Id,
          Answers: [],
        };
        question.Answers.forEach((answer) => {
          let selectedAnswer: PredictorSelectionModel = {
            AnswerId: answer.Id,
            CustomerValue: answer.Value && +answer.Value > 0 ? answer.Value : '0',
          };
          predictorQuestionSelection.Answers.push(selectedAnswer);
        });

        this.predictorSelectionModel.Questions.push(predictorQuestionSelection);
      }
    });

    if (this.isExternal) {
      this.externalPredictorSubmitted.emit(this.allAnswersSubmitted());
    }
  }

  onAnswerSelected(questionId: number, event: any): void {
    let predictorAnswerSelection: PredictorSelectionModel[] = [];
    let predictorQuestionSelection: PredictorSelectionQuestionViewModel;
    let predictorQuestion = this.predictor.Questions.find((question) => question.Id == questionId);
    let questionType = predictorQuestion?.AnswerDisplayType;

    let question = this.predictorSelectionModel.Questions.find((question) => question.QuestionId == questionId);
    if (questionType !== AnswerDisplay.Score) {
      if (
        questionType == AnswerDisplay.ImageAndText ||
        questionType == AnswerDisplay.Text ||
        questionType === AnswerDisplay.VideoCarousel
      ) {
        predictorAnswerSelection = [{ AnswerId: event.Id, CustomerValue: event.Id }];
      } else {
        predictorAnswerSelection = [{ AnswerId: event.answerId, CustomerValue: event.customerValue }];
      }

      if (question) {
        question.Answers = predictorAnswerSelection;
      } else {
        predictorQuestionSelection = { QuestionId: questionId, Answers: predictorAnswerSelection };
        this.predictorSelectionModel.Questions.push(predictorQuestionSelection);
      }
    } else {
      if (question) {
        let answer = question.Answers.find((answer) => answer.AnswerId == event.answerId);
        if (answer) {
          answer.CustomerValue = event.customerValue;
        } else {
          question?.Answers.push({ AnswerId: event.answerId, CustomerValue: event.customerValue });
        }
      } else {
        let predictorQuestions = this.predictor.Questions.find((question) => question.Id == questionId);
        let otherAnswer = predictorQuestions?.Answers.find((answer) => answer.Id != event.answerId);

        predictorQuestionSelection = {
          QuestionId: questionId,
          Answers: [
            { AnswerId: event.answerId, CustomerValue: event.customerValue },
            { AnswerId: otherAnswer?.Id, CustomerValue: 0 },
          ],
        };
        this.predictorSelectionModel.Questions.push(predictorQuestionSelection);
      }
    }

    console.log(question?.Answers);
    this.unsavedChangesEvent.emit(true); // Emit true when there are unsaved changes
  }

  savePrediction(): void {
    this.isClickOnsavePrediction = true;
    this.loadingState = true;
    firstValueFrom(this.gamesGroupService.submitPredictorAnswers(this.groupId, this.predictorSelectionModel, this.isExternal))
      .then((res) => {
        setTimeout(() => {
          this.loadingState = false;
          this.unsavedChangesEvent.emit(false);
          this.editPredictions = false;
          if (!this.isExternal) {
            this.openThanksForPlayingModel();
          } else {
            this.externalPredictorSubmitted.emit(true);
          }
        }, 1000);
      })
      .catch((error) => {
        console.error('Error submitting predictions:', error);
        this.loadingState = false;
      });
  }

  canSubmitPrediction(): boolean {
    if (this.predictorExpired) {
      return false; // Cannot submit if the predictor has expired
    }

    return this.allAnswersSelected() || this.allAnswersSubmitted(); // Returns true if all questions have answers
  }

  allAnswersSubmitted(): boolean {
    return this.predictor.Questions.every((question) => {
      return question.Answers.find((answer) => answer.Value || (answer as UnprocessedPredictorAnswer).UserSelected);
    });
  }

  allAnswersSelected(): boolean {
    return this.predictor.Questions.every((question) => {
      const selectedAnswer = this.predictorSelectionModel.Questions.find((model) => model.QuestionId === question.Id);
      return selectedAnswer && selectedAnswer.Answers.length > 0;
    });
  }

  predictorEnded(): void {
    this.predictorExpired = true;
  }

  openThanksForPlayingModel(): Promise<boolean> {
    this.modalRef = this.modalService.open(GamePredictorThanksForPlayingModalComponent, {
      centered: true,
      windowClass: 'fgbcl-game-confirming-unsaved-model-custom-style .modal-dialog',
    });

    this.modalRef.componentInstance.endDate = this.predictor.EndDate;
    this.modalRef.componentInstance.redirectPageURL = this.sharedRedirectURL;
    this.modalRef.componentInstance.onDestroy.subscribe((res: string) => {
      if (res == 'edit') {
        this.modalRef.close();
        return;
      }
      this.closeModal.emit();
    });
    return new Promise<boolean>((resolve) => {
      this.modalRef.result.then(
        (result) => {
          if (result === 'confirm') {
            resolve(true);
          } else {
            resolve(false);
          }
        },
        () => {
          resolve(false);
        }
      );
    });
  }
}
